import { jwtDecode } from 'jwt-decode';
import api from './api';
import { crossTabSession } from '../utils/crossTabSession';

// CORREÇÃO: Interfaces consolidadas
interface TokenPayload {
  id: number;
  nome: string;
  cargo: string;
  cod_empresa: number;
  iat: number;
  exp: number;
}

interface UserData {
  id: number;
  nome: string;
  email: string;
  cargo: string;
  cod_empresa: number;
}

interface Empresa {
  id: number;
  nome: string;
}

interface AuthResponse {
  token: string;
  usuario: UserData;
  empresa: Empresa;
}

/**
 * NOVA CLASSE DE AUTENTICAÇÃO COM SISTEMA CROSS-TAB ROBUSTO
 * Resolve todos os problemas de sessão entre abas
 */
export class AuthService {
  private token: string | null = null;
  private usuario: UserData | null = null;
  private empresa: Empresa | null = null;
  
  // Constantes para storage
  private readonly TOKEN_KEY = 'token';
  private readonly USER_KEY = 'user';
  private readonly EMPRESA_KEY = 'empresa';
  
  // Controle de inicialização
  private isInitialized = false;
  private initializationPromise: Promise<void> | null = null;
  
  constructor() {
    this.initialize();
  }

  /**
   * Inicialização assíncrona e segura
   */
  private async initialize(): Promise<void> {
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = this.performInitialization();
    return this.initializationPromise;
  }

  /**
   * Realizar inicialização
   */
  private async performInitialization(): Promise<void> {
    try {
      // Aguardar inicialização do cross-tab session
      const hasValidSession = await crossTabSession.initializeSession();
      
      if (hasValidSession) {
        this.loadSessionData();
      } else {
        }
      
      this.isInitialized = true;
      } catch (error) {
      console.error('[AUTH] Erro durante inicialização:', error);
      this.isInitialized = true; // Marcar como inicializado mesmo com erro
    }
  }

  /**
   * Aguardar inicialização
   */
  public async waitForInitialization(): Promise<void> {
    if (this.isInitialized) {
      return;
    }
    
    if (this.initializationPromise) {
      await this.initializationPromise;
    }
  }

  /**
   * Carregar dados da sessão
   */
  private loadSessionData(): void {
    try {
      const token = sessionStorage.getItem(this.TOKEN_KEY);
      const userData = sessionStorage.getItem(this.USER_KEY);
      const empresaData = sessionStorage.getItem(this.EMPRESA_KEY);
      
      if (token && userData && empresaData) {
        this.token = token;
        this.usuario = JSON.parse(userData);
        this.empresa = JSON.parse(empresaData);
        
        // Configurar header da API
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        }
    } catch (error) {
      console.error('[AUTH] Erro ao carregar dados da sessão:', error);
    }
  }

  /**
   * LOGIN - Consolidado e com sincronização cross-tab
   */
  public async login(empresa: string, usuario: string, senha: string): Promise<boolean> {
    try {
      // Limpeza completa ANTES do login
      this.clearAllData();
      
      // Realizar login
      const response = await api.post<AuthResponse>('/auth/login', {
        empresa,
        usuario,
        senha
      });
      
      if (!response.data?.token) {
        console.error('[AUTH] Login falhou: resposta da API não contém token');
        return false;
      }
      
      const { token, usuario: userData, empresa: empresaData } = response.data;
      
      // VALIDAÇÃO CRÍTICA: Verificar consistência
      if (!userData?.cod_empresa || !empresaData?.id) {
        console.error('[AUTH] ERRO: Dados inconsistentes recebidos do servidor');
        return false;
      }
      
      if (userData.cod_empresa !== empresaData.id) {
        console.error('[AUTH] ERRO: cod_empresa do usuário não coincide com ID da empresa', {
          codEmpresaUsuario: userData.cod_empresa,
          idEmpresa: empresaData.id
        });
        return false;
      }
      
      // Validar token
      try {
        const decoded = jwtDecode<TokenPayload>(token);
        
        if (decoded.cod_empresa !== userData.cod_empresa) {
          console.error('[AUTH] ERRO: Token contém cod_empresa diferente dos dados do usuário');
          return false;
        }
      } catch (tokenError) {
        console.error('[AUTH] ERRO ao decodificar token:', tokenError);
        return false;
      }
      
      // Salvar dados validados LOCALMENTE
      this.storeAuthData(token, userData, empresaData);
      
      // Sincronizar com todas as abas via crossTabSession
      crossTabSession.login(token, userData, empresaData);
      
      // Disparar evento de login para componentes
      this.dispatchAuthEvent('login', { usuario: userData, empresa: empresaData });
      
      return true;
    } catch (error: any) {
      console.error('[AUTH] Erro durante login:', error);
      console.error('[AUTH] Detalhes do erro:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        url: error.config?.url,
        baseURL: error.config?.baseURL
      });
      this.clearAllData();
      throw error;
    }
  }

  /**
   * LOGOUT - Consolidado e com sincronização cross-tab
   */
  public logout(): void {
    // Disparar evento de logout ANTES de limpar dados
    this.dispatchAuthEvent('logout');
    
    // Usar crossTabSession para logout sincronizado
    crossTabSession.logout();
    
    // Limpar dados locais
    this.clearAllData();
    
    window.location.href = '/login';
  }

  /**
   * VERIFICAÇÃO DE AUTENTICAÇÃO - Simplificada e robusta
   */
  public isAuthenticated(): boolean {
    try {
      // Aguardar inicialização se necessário
      if (!this.isInitialized) {
        return false;
      }
      
      // Usar verificação cross-tab
      const crossTabAuth = crossTabSession.isAuthenticated();
      
      if (!crossTabAuth) {
        return false;
      }
      
      // Verificação local adicional
      const token = this.getToken();
      
      if (!token) {
        return false;
      }

      const decoded = jwtDecode<TokenPayload>(token);
      const currentTime = Date.now() / 1000;
      
      // Margem de 30 segundos para evitar problemas de sincronização
      const isValid = decoded.exp > (currentTime + 30);
      
      if (!isValid) {
        console.warn('[AUTH] Token expirado - limpando sessão', {
          expira: new Date(decoded.exp * 1000).toLocaleTimeString(),
          atual: new Date().toLocaleTimeString(),
          diferenca: Math.round(decoded.exp - currentTime) + 's'
        });
        this.clearAllData();
      }
      
      return isValid;
    } catch (error) {
      console.error('[AUTH] Erro ao validar token:', error);
      this.clearAllData();
      return false;
    }
  }

  /**
   * Obter token
   */
  public getToken(): string | null {
    if (this.token) {
      return this.token;
    }
    
    const sessionToken = sessionStorage.getItem(this.TOKEN_KEY);
    if (sessionToken) {
      this.token = sessionToken;
      return sessionToken;
    }
    
    return null;
  }

  /**
   * Obter dados do usuário
   */
  public getUsuario(): UserData | null {
    if (this.usuario) {
      return this.usuario;
    }
    
    const userData = sessionStorage.getItem(this.USER_KEY);
    if (userData) {
      try {
        this.usuario = JSON.parse(userData);
        return this.usuario;
      } catch (error) {
        console.error('[AUTH] Erro ao recuperar dados do usuário:', error);
        return null;
      }
    }
    
    return null;
  }

  /**
   * Obter dados da empresa
   */
  public getEmpresa(): Empresa | null {
    if (this.empresa) {
      return this.empresa;
    }
    
    const empresaData = sessionStorage.getItem(this.EMPRESA_KEY);
    if (empresaData) {
      try {
        this.empresa = JSON.parse(empresaData);
        return this.empresa;
      } catch (error) {
        console.error('[AUTH] Erro ao recuperar dados da empresa:', error);
        return null;
      }
    }
    
    return null;
  }

  /**
   * Verificar se é administrador
   */
  public isAdmin(): boolean {
    const usuario = this.getUsuario();
    return usuario?.cargo === 'admin';
  }

  /**
   * Disparar evento customizado de mudança de autenticação
   */
  private dispatchAuthEvent(type: 'login' | 'logout', data?: any): void {
    const event = new CustomEvent('authChange', {
      detail: { type, data, timestamp: new Date().toISOString() }
    });
    
    console.log('[AUTH] Disparando evento de autenticação:', { type, timestamp: new Date().toISOString() });
    window.dispatchEvent(event);
  }

  /**
   * MÉTODO PRIVADO: Armazenar dados de autenticação
   */
  private storeAuthData(token: string, usuario: UserData, empresa: Empresa): void {
    // Armazenar em memória
    this.token = token;
    this.usuario = usuario;
    this.empresa = empresa;
    
    // Armazenar em sessionStorage
    sessionStorage.setItem(this.TOKEN_KEY, token);
    sessionStorage.setItem(this.USER_KEY, JSON.stringify(usuario));
    sessionStorage.setItem(this.EMPRESA_KEY, JSON.stringify(empresa));
    
    // Configurar header da API
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    
    }

  /**
   * MÉTODO PRIVADO: Limpar todos os dados
   */
  private clearAllData(): void {
    // Limpar memória
    this.token = null;
    this.usuario = null;
    this.empresa = null;
    
    // Limpar sessionStorage
    sessionStorage.removeItem(this.TOKEN_KEY);
    sessionStorage.removeItem(this.USER_KEY);
    sessionStorage.removeItem(this.EMPRESA_KEY);
    
    // Limpar header da API
    if (api.defaults.headers.common) {
      delete api.defaults.headers.common['Authorization'];
    }
    
    }

  /**
   * Alterar senha do usuário atual
   */
  public async alterarSenha(userId: number, senhaAtual: string, novaSenha: string): Promise<void> {
    try {
      const response = await api.put(`/usuarios/${userId}/alterar-senha`, {
        senhaAtual,
        novaSenha
      });
      
      if (response.status === 200) {
        } else {
        throw new Error('Falha ao alterar senha');
      }
    } catch (error: any) {
      console.error('[AUTH] Erro ao alterar senha:', error);
      
      // Tratar erros específicos do backend
      if (error.response?.status === 400) {
        throw new Error(error.response.data.mensagem || 'Dados inválidos');
      } else if (error.response?.status === 403) {
        throw new Error('Você não tem permissão para alterar esta senha');
      } else if (error.response?.data?.mensagem) {
        throw new Error(error.response.data.mensagem);
      } else {
        throw new Error('Erro ao alterar senha. Tente novamente.');
      }
    }
  }

  /**
   * Métodos de compatibilidade (vazios - não necessários com cross-tab)
   */
  public setupLogoutSync(): void {
    // Não necessário - crossTabSession cuida disso
    console.log('[AUTH] setupLogoutSync() - delegado para crossTabSession');
  }

  public removeLogoutSync(): void {
    // Não necessário - crossTabSession cuida disso
    console.log('[AUTH] removeLogoutSync() - delegado para crossTabSession');
  }
}

// Exportar instância única (singleton)
export const authService = new AuthService();

// Exportação padrão
export default authService;
